"use client";

import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import {
  Badge,
  Button,
  Input,
  NativeSelect,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stocks/ui";
import {
  ITEM_CATEGORY_VALUES,
  type ItemCategory,
  type ListStocksQuery,
  type StockItemResponse,
} from "@stocks/shared";

import { usePaginatedList } from "@/shared/libs/query";
import { ITEM_CATEGORY_LABELS, ItemCategoryBadge } from "@/entities/item";
import {
  ReplenishBadge,
  formatQuantity,
  needsReplenishment,
} from "@/entities/stock";

const PAGE_SIZE = 20;
const COLS = 9;

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("ko-KR") : "-";

/** 재고 목록: 품목 합계 + 로트/위치별 보유·예약·가용, 가용 < 안전재고 이면 '보충 필요' */
export const StockTable = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<ItemCategory | "">("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const query: ListStocksQuery = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    category: category || undefined,
  };
  const { data, isLoading, isError } = usePaginatedList<
    StockItemResponse,
    ListStocksQuery
  >("stocks", "/v1/stocks", query);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const rows = (data?.items ?? []).filter(
    (r) => !onlyLow || needsReplenishment(r.totals.available, r.item.safetyStock),
  );
  const lowCount = (data?.items ?? []).filter((r) =>
    needsReplenishment(r.totals.available, r.item.safetyStock),
  ).length;

  return (
    <div className='flex flex-col gap-4'>
      <form
        className='flex flex-wrap items-center gap-2'
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(keyword.trim());
          setPage(1);
        }}
      >
        <Input
          className='w-64'
          placeholder='이름 / 코드 / 규격 검색'
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button type='submit'>검색</Button>
        <NativeSelect
          aria-label='카테고리 필터'
          value={category}
          onChange={(e) => {
            setCategory(e.target.value as ItemCategory | "");
            setPage(1);
          }}
        >
          <option value=''>전체 분류</option>
          {ITEM_CATEGORY_VALUES.map((c) => (
            <option key={c} value={c}>
              {ITEM_CATEGORY_LABELS[c]}
            </option>
          ))}
        </NativeSelect>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='checkbox'
            checked={onlyLow}
            onChange={(e) => setOnlyLow(e.target.checked)}
          />
          보충 필요만 보기
        </label>
        {lowCount > 0 && (
          <Badge variant='destructive'>이 페이지 보충 필요 {lowCount}건</Badge>
        )}
      </form>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-10' />
            <TableHead>코드</TableHead>
            <TableHead>품목명</TableHead>
            <TableHead>분류</TableHead>
            <TableHead className='text-right'>보유</TableHead>
            <TableHead className='text-right'>예약</TableHead>
            <TableHead className='text-right'>가용</TableHead>
            <TableHead className='text-right'>안전재고</TableHead>
            <TableHead>상태</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={COLS} className='text-center text-muted-foreground'>
                불러오는 중…
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell colSpan={COLS} className='text-center text-destructive'>
                재고를 불러오지 못했습니다.
              </TableCell>
            </TableRow>
          )}
          {data && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLS} className='text-center text-muted-foreground'>
                {onlyLow
                  ? "이 페이지에 보충이 필요한 품목이 없습니다."
                  : "조건에 맞는 재고가 없습니다."}
              </TableCell>
            </TableRow>
          )}
          {rows.map(({ item, totals, lots }) => {
            const open = expanded.has(item.id);
            const low = needsReplenishment(totals.available, item.safetyStock);
            return (
              <Fragment key={item.id}>
                <TableRow className={low ? "bg-destructive/5" : undefined}>
                  <TableCell>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon-sm'
                      aria-label={open ? "로트 접기" : "로트 펼치기"}
                      aria-expanded={open}
                      disabled={lots.length === 0}
                      onClick={() => toggle(item.id)}
                    >
                      {open ? (
                        <ChevronDown className='size-4' />
                      ) : (
                        <ChevronRight className='size-4' />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell className='font-medium'>{item.name}</TableCell>
                  <TableCell>
                    <ItemCategoryBadge category={item.category} />
                  </TableCell>
                  <TableCell className='text-right'>
                    {formatQuantity(totals.onHand)} {item.baseUnit}
                  </TableCell>
                  <TableCell className='text-right'>
                    {formatQuantity(totals.reserved)}
                  </TableCell>
                  <TableCell className='text-right font-medium'>
                    {formatQuantity(totals.available)}
                  </TableCell>
                  <TableCell className='text-right'>
                    {formatQuantity(item.safetyStock)}
                  </TableCell>
                  <TableCell>
                    <ReplenishBadge
                      available={totals.available}
                      safetyStock={item.safetyStock}
                    />
                  </TableCell>
                </TableRow>
                {open &&
                  lots.map((lot) => (
                    <TableRow key={lot.lotId} className='bg-muted/40 text-sm'>
                      <TableCell />
                      <TableCell colSpan={2}>
                        <span className='font-medium'>{lot.lotNo}</span>
                        {lot.supplierLotNo && (
                          <span className='ml-2 text-muted-foreground'>
                            ({lot.supplierLotNo})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {lot.location
                          ? `${lot.location.code} · ${lot.location.name}`
                          : "위치 미지정"}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatQuantity(lot.onHand)}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatQuantity(lot.reserved)}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatQuantity(lot.available)}
                      </TableCell>
                      <TableCell colSpan={2} className='text-muted-foreground'>
                        {lot.vendor?.name ?? "-"} · 입고 {formatDate(lot.receivedAt)}
                        {lot.expiresAt && ` · 유효 ${formatDate(lot.expiresAt)}`}
                      </TableCell>
                    </TableRow>
                  ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {data && (
        <div className='flex items-center justify-end gap-3 text-sm'>
          <span className='text-muted-foreground'>총 {data.meta.total}건</span>
          <Button
            variant='outline'
            size='sm'
            disabled={!data.meta.hasPrev}
            onClick={() => setPage((p) => p - 1)}
          >
            이전
          </Button>
          <span>
            {data.meta.page} / {data.meta.totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={!data.meta.hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
};
